import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";
import styled from "@emotion/styled";
import { Button, Checkbox, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import { FC, useState } from "react";

type ResetOptions = {
  // Space account
  resetImportCta: boolean;
  resetIcpRfi: boolean;
  resetHeadsUpCoachMark: boolean;
  resetOnboardingChecklist: boolean;
  resetAppCallouts: boolean;
  resetSpaceAccountSettings: boolean;
};

type ResetLockedOn = Partial<Record<keyof ResetOptions, boolean>>;

const DEFAULT_RESET_OPTIONS: ResetOptions = {
  resetImportCta: true,
  resetIcpRfi: true,
  resetHeadsUpCoachMark: true,
  resetOnboardingChecklist: true,
  resetAppCallouts: true,
  resetSpaceAccountSettings: true,
};

export const SpaceAccountResetDialog: FC<{
  spaceAccountId: string;
  onAfterReset?: () => Promise<void> | void;
}> = ({ spaceAccountId, onAfterReset }) => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetOptions, setResetOptions] = useState<ResetOptions>(DEFAULT_RESET_OPTIONS);
  const [isResetting, setIsResetting] = useState(false);
  const [lockedOn, setLockedOn] = useState<ResetLockedOn>({});
  const [isLoadingResetState, setIsLoadingResetState] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetConfirmError, setResetConfirmError] = useState<string | null>(null);

  const isResetOptionLockedOn = (key: keyof ResetOptions) => lockedOn[key] === true;

  const allResetOptionsSelected = (Object.keys(resetOptions) as (keyof ResetOptions)[]).every(
    key => resetOptions[key] === true
  );

  const setAllResetOptionsSelected = (value: boolean) => {
    setResetOptions(prev => {
      const next: ResetOptions = { ...prev };
      (Object.keys(next) as (keyof ResetOptions)[]).forEach(key => {
        if (isResetOptionLockedOn(key)) {
          next[key] = true;
          return;
        }
        next[key] = value;
      });
      return next;
    });
  };

  const openResetDialog = () => {
    setLockedOn({});
    setIsLoadingResetState(true);
    setResetOptions(DEFAULT_RESET_OPTIONS);
    setResetConfirmText("");
    setResetConfirmError(null);
    setIsResetDialogOpen(true);

    api
      .get("/internal/v1/space-accounts/{space_account_id}/reset-state", {
        params: { path: { space_account_id: spaceAccountId } },
      })
      .then(response => {
        const data = response.data;
        if (!data) return;

        const nextLockedOn: ResetLockedOn = {
          resetImportCta: data.import_cta_is_active,
          resetIcpRfi: data.icp_rfi_is_active,
          resetHeadsUpCoachMark: data.heads_up_coach_mark_is_active,
          resetOnboardingChecklist: data.onboarding_checklist_is_active,
          resetAppCallouts: data.app_callouts_are_default,
          resetSpaceAccountSettings: data.space_account_settings_are_default,
        };

        setLockedOn(nextLockedOn);
        setResetOptions(prev => {
          const next: ResetOptions = { ...prev };
          (Object.keys(nextLockedOn) as (keyof ResetOptions)[]).forEach(key => {
            if (nextLockedOn[key]) {
              next[key] = true;
            }
          });
          return next;
        });
      })
      .catch(err => {
        console.error("Failed to fetch reset state:", err);
      })
      .finally(() => {
        setIsLoadingResetState(false);
      });
  };

  const resetConfirmTextIsValid = resetConfirmText.trim().toUpperCase() === "CONFIRM";

  const handleRunReset = async () => {
    setResetConfirmError(null);
    if (!resetConfirmTextIsValid) {
      setResetConfirmError("Type CONFIRM to proceed.");
      return;
    }

    setIsResetting(true);
    try {
      const response = await api.post("/internal/v1/space-accounts/{space_account_id}/reset", {
        params: { path: { space_account_id: spaceAccountId } },
        body: {
          // Space account
          reset_import_cta: resetOptions.resetImportCta,
          reset_icp_rfi: resetOptions.resetIcpRfi,
          reset_heads_up_coach_mark: resetOptions.resetHeadsUpCoachMark,
          reset_onboarding_checklist: resetOptions.resetOnboardingChecklist,
          reset_app_callouts: resetOptions.resetAppCallouts,
          reset_space_account_settings: resetOptions.resetSpaceAccountSettings,

          // Account (intentionally excluded from this dialog)
          reset_account_platform_onboarding_info: false,
          reset_has_interacted_with_v2_platform: false,
          reset_has_onboarded_with_v2_platform: false,
          cancel_new_account_onboarding_workflow: false,
          cancel_new_v2_account_onboarding_workflow: false,
        },
      });

      if (!response.data?.success) {
        toastModule.triggerToast({
          content: "Reset completed with errors. See console for details (and response payload).",
        });
        console.error("Reset completed with errors:", response.data);
      } else {
        toastModule.triggerToast({ content: "Reset completed successfully." });
      }

      await onAfterReset?.();
      setIsResetDialogOpen(false);
    } catch (error) {
      toastModule.triggerToast({ content: "Failed to reset. Check console for details." });
      console.error("Failed to reset space account:", error);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={openResetDialog}>
        Reset…
      </Button>
      <Dialog.Root open={isResetDialogOpen} onOpenChange={open => setIsResetDialogOpen(open)}>
        <Dialog.Content style={{ maxWidth: "720px" }}>
          <Dialog.Title>Reset space account</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Select which reset actions to run. This is intended for internal testing and will be
            recorded for auditing.
          </Dialog.Description>

          <WarningBox>
            <Text weight="bold">Warning</Text>
            <Text size="2" color="gray">
              Avoid using this for live users. This can change onboarding and guidance state.
            </Text>
          </WarningBox>

          {isLoadingResetState ? (
            <Text size="2" color="gray">
              Loading current onboarding state…
            </Text>
          ) : null}

          <SectionHeading size="2" mb="2">
            Select all (default)
          </SectionHeading>
          <CheckboxRow>
            <Checkbox
              checked={allResetOptionsSelected}
              onCheckedChange={value => setAllResetOptionsSelected(value === true)}
              id="reset-select-all"
            />
            <Text as="label" htmlFor="reset-select-all">
              Reset everything
            </Text>
          </CheckboxRow>

          <SectionHeading size="2" mb="2" mt="4">
            Space account
          </SectionHeading>
          <OptionsGrid>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetImportCta}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetImportCta: value === true }))
                }
                disabled={isResetOptionLockedOn("resetImportCta")}
                id="reset-import-cta"
              />
              <Text as="label" htmlFor="reset-import-cta">
                Import CTA
              </Text>
            </CheckboxRow>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetIcpRfi}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetIcpRfi: value === true }))
                }
                disabled={isResetOptionLockedOn("resetIcpRfi")}
                id="reset-icp-rfi"
              />
              <Text as="label" htmlFor="reset-icp-rfi">
                ICP RFI
              </Text>
            </CheckboxRow>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetHeadsUpCoachMark}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetHeadsUpCoachMark: value === true }))
                }
                disabled={isResetOptionLockedOn("resetHeadsUpCoachMark")}
                id="reset-heads-up-coach-mark"
              />
              <Text as="label" htmlFor="reset-heads-up-coach-mark">
                Heads Up coach mark
              </Text>
            </CheckboxRow>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetOnboardingChecklist}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetOnboardingChecklist: value === true }))
                }
                disabled={isResetOptionLockedOn("resetOnboardingChecklist")}
                id="reset-onboarding-checklist"
              />
              <Text as="label" htmlFor="reset-onboarding-checklist">
                Onboarding checklist + tasks
              </Text>
            </CheckboxRow>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetAppCallouts}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetAppCallouts: value === true }))
                }
                disabled={isResetOptionLockedOn("resetAppCallouts")}
                id="reset-app-callouts"
              />
              <Text as="label" htmlFor="reset-app-callouts">
                App callouts (dismissals)
              </Text>
            </CheckboxRow>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetSpaceAccountSettings}
                onCheckedChange={value =>
                  setResetOptions(prev => ({ ...prev, resetSpaceAccountSettings: value === true }))
                }
                disabled={isResetOptionLockedOn("resetSpaceAccountSettings")}
                id="reset-space-account-settings"
              />
              <Text as="label" htmlFor="reset-space-account-settings">
                Space account settings (voice/calendar)
              </Text>
            </CheckboxRow>
          </OptionsGrid>

          <SectionHeading size="2" mb="2" mt="4">
            Confirmation
          </SectionHeading>
          <Text size="2" color="gray">
            Type <b>CONFIRM</b> to run the selected resets.
          </Text>
          <TextField.Root
            value={resetConfirmText}
            onChange={event => {
              setResetConfirmText(event.currentTarget.value);
              if (resetConfirmError) setResetConfirmError(null);
            }}
            onKeyDown={event => {
              if (event.key !== "Enter") return;
              event.preventDefault();

              if (isResetting || isLoadingResetState) return;
              if (!resetConfirmTextIsValid) return;

              void handleRunReset();
            }}
            placeholder="Type CONFIRM to acknowledge the warning"
            mt="2"
          />
          {resetConfirmError ? <ErrorText size="2">{resetConfirmError}</ErrorText> : null}

          <DialogActions>
            <Flex gap="2" justify="end">
              <Button
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
                disabled={isResetting || isLoadingResetState}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRunReset}
                disabled={isResetting || isLoadingResetState || !resetConfirmTextIsValid}
                color="gray"
              >
                {isLoadingResetState ? "Loading…" : isResetting ? "Resetting…" : "Run reset"}
              </Button>
            </Flex>
          </DialogActions>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

const CheckboxRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const SectionHeading = styled(Text)`
  display: block;
  font-weight: 600;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const DialogActions = styled.div`
  margin-top: 20px;
`;

const WarningBox = styled.div`
  border: 1px solid var(--gray-6);
  background: var(--gray-2);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(Text)`
  color: var(--red-11);
  display: block;
  margin-top: 6px;
`;
